import { defaultWebsiteId, MANUALLY_TRANSLATED_LANGUAGES } from '@va/constants';
import { DatePickerShortcut, DateRangeKey } from '@va/types/time';
import { StorageKeysManager } from '@va/util/misc';
import { debounce as lodashDebounce } from 'lodash';
import moment, { Moment } from 'moment';
import { RefObject } from 'react';
import {
  ALL_DATEPICKER_SHORTCUTS,
  findMatchingShortcut,
  FREE_PLAN_HISTORY_DAYS,
  fromDayjsToMoment,
  fromMomentToDayjs,
  getDateRange,
  getShortcutDateRange,
  isDateRangeKey,
} from '../date';
import { SessionStorage } from '../storage';
import { capitalizeFirstLetter } from '../string';

export const renderIf = (predicate: boolean) => (component: React.ReactNode) => (predicate ? component : null);

export function getCountryCodeByLocale(locale: string) {
  switch (locale.toLowerCase()) {
    case 'en':
      return 'US';
    case 'hi':
      return 'IN';
    case 'ja':
      return 'JP';
    case 'cs':
      return 'CZ';
    case 'ko':
      return 'KR';
    case 'da':
      return 'DK';
    case 'sv':
      return 'SE';
    case 'zh':
      return 'CN';
    case 'uk':
      return 'UA';
    case 'vi':
      return 'VN';
    case 'he':
      return 'IL';
    case 'el':
      return 'GR';
    case 'et':
      return 'EE';
    case 'ar':
      return 'SA';
    default:
      return locale.toUpperCase();
  }
}

export function getTypeOfVisitor(visitorType: number) {
  switch (visitorType) {
    case 1:
      return 'returningVisitor';
    case 2:
      return 'conversionVisitor';
    default:
      return 'newVisitor';
  }
}

export function debounce(callback: () => any, delay: number) {
  return lodashDebounce(() => callback(), delay);
}

export function decodeMalformedURIComponent(input: string) {
  try {
    return decodeURIComponent(input);
  } catch (err) {
    const fixedEncoding = encodeURI(input).replace(/%5B/g, '[').replace(/%5D/g, ']');
    return decodeURIComponent(fixedEncoding);
  }
}

/** @deprecated Will only be used in one place, move it there */
export const getAdCampaignLabel = (value: string, translate: (k: string) => string) => {
  return value ? capitalizeFirstLetter(value) : '[' + translate('ads.utm.notProvided.text') + ']';
};

export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

export const isTextEllipsed = (contentRef: RefObject<HTMLElement | null>) => {
  if (!contentRef.current) return false;
  const { scrollWidth, offsetWidth } = contentRef.current;
  return scrollWidth - offsetWidth > 2;
};

export const setResizeObserver = (elementRef: RefObject<HTMLElement | null>, onResize: () => void) => {
  if (!elementRef?.current) return;
  const baseTabContent = elementRef && elementRef.current;
  const resizeObserver = new ResizeObserver(() => {
    onResize();
  });
  resizeObserver.observe(baseTabContent);
  return () => {
    resizeObserver.unobserve(baseTabContent);
  };
};

export const isManuallyTranslatedLanguage = (locale: string) => {
  return MANUALLY_TRANSLATED_LANGUAGES.includes(locale);
};

export function getHumanReadableFilesize(size: number) {
  const sizes = [' Bytes', ' KB', ' MB', ' GB', ' TB', ' PB', ' EB', ' ZB', ' YB'];

  for (let i = 1; i < sizes.length; i++) {
    if (size < Math.pow(1024, i)) return Math.round((size / Math.pow(1024, i - 1)) * 100) / 100 + sizes[i - 1];
  }
  return size;
}

const setCurrentDateToSessionStorage = () => {
  SessionStorage.setItem(StorageKeysManager.getTodayDateKey(), JSON.stringify(moment()));
};

export const storeTimePeriodToLocalStorage = ({
  from,
  until,
  websiteId,
  hasFreePlan,
  installDate,
  dateRangeKey,
}: {
  websiteId: string;
  from: moment.Moment;
  until: moment.Moment;
  hasFreePlan: boolean;
  installDate: number;
  dateRangeKey?: DateRangeKey;
}) => {
  if (!from || !until || websiteId === defaultWebsiteId) return;

  const minDate = getDatepickerMinDate({
    hasFreePlan,
    installDate,
  });

  const matchingDateShortcut = findMatchingShortcut(
    ALL_DATEPICKER_SHORTCUTS,
    [fromMomentToDayjs(from), fromMomentToDayjs(until)],
    fromMomentToDayjs(minDate),
  );

  setCurrentDateToSessionStorage();
  localStorage.setItem(
    StorageKeysManager.getTimePeriodKey(websiteId),
    JSON.stringify({
      from: from.toISOString(),
      until: until.toISOString(),
      dateShortcut: matchingDateShortcut?.key,
      dateRangeKey,
    }),
  );
};

export const getTimePeriodFromStorage = (
  websiteId: string,
  websiteTimezone: string,
  websiteInstallDate: number,
  hasActiveFreePlan: boolean,
): { from: moment.Moment; until: moment.Moment; dateRangeKey?: DateRangeKey } | undefined => {
  const storedTimePeriod = localStorage.getItem(StorageKeysManager.getTimePeriodKey(websiteId));

  if (!storedTimePeriod || storedTimePeriod === 'null') return;

  try {
    const parsedTimePeriod = JSON.parse(storedTimePeriod) as {
      from?: string;
      until?: string;
      dateShortcut?: DateRangeKey;
      dateRangeKey?: DateRangeKey;
    };
    if (typeof parsedTimePeriod != 'object') {
      throw new Error('invalid time period');
    }

    // Always recalculated relative to today, so it doesn't need the day change handling below
    if (isDateRangeKey(parsedTimePeriod.dateRangeKey)) {
      const minDate = getDatepickerMinDate({
        hasFreePlan: hasActiveFreePlan,
        installDate: websiteInstallDate,
        timezone: websiteTimezone,
      });
      const { start, end } = getShortcutDateRange(
        parsedTimePeriod.dateRangeKey,
        fromMomentToDayjs(minDate),
        websiteTimezone,
      );

      return {
        from: fromDayjsToMoment(start),
        until: fromDayjsToMoment(end),
        dateRangeKey: parsedTimePeriod.dateRangeKey,
      };
    }

    let matchingShortcut: DatePickerShortcut | undefined;
    let shortcutDateRange: { start: Moment; end: Moment } | undefined;

    if (parsedTimePeriod.dateShortcut) {
      matchingShortcut = ALL_DATEPICKER_SHORTCUTS.find((s) => s.key === parsedTimePeriod.dateShortcut);

      const minDate = getDatepickerMinDate({
        hasFreePlan: hasActiveFreePlan,
        installDate: websiteInstallDate,
        timezone: websiteTimezone,
      });

      const dayjsDateRange = getDateRange(
        parsedTimePeriod.dateShortcut,
        matchingShortcut?.diff,
        fromMomentToDayjs(minDate),
        window.timezone,
      );

      shortcutDateRange = {
        start: fromDayjsToMoment(dayjsDateRange.start),
        end: fromDayjsToMoment(dayjsDateRange.end),
      };
    }

    const from = (shortcutDateRange?.start ?? moment(parsedTimePeriod.from)).tz(window.timezone);
    const until = (shortcutDateRange?.end ?? moment(parsedTimePeriod.until)).tz(window.timezone);

    const todaysSessionDate = SessionStorage.getItem(StorageKeysManager.getTodayDateKey());

    if (!todaysSessionDate) {
      return { from, until };
    }

    const currentSessionMomentDate = moment(JSON.parse(todaysSessionDate)).tz(websiteTimezone);

    if (!currentSessionMomentDate.isSame(moment().tz(websiteTimezone), 'day')) {
      if (parsedTimePeriod.dateShortcut) {
        const minDate = getDatepickerMinDate({
          hasFreePlan: hasActiveFreePlan,
          installDate: websiteInstallDate,
          timezone: websiteTimezone,
        });

        const dayjsDateRange = getDateRange(
          parsedTimePeriod.dateShortcut,
          matchingShortcut?.diff,
          fromMomentToDayjs(minDate),
          websiteTimezone,
        );

        shortcutDateRange = {
          start: fromDayjsToMoment(dayjsDateRange.start),
          end: fromDayjsToMoment(dayjsDateRange.end),
        };
      }

      return updateSelectedPeriod(
        (shortcutDateRange?.start ?? moment(parsedTimePeriod.from)).tz(websiteTimezone),
        (shortcutDateRange?.end ?? moment(parsedTimePeriod.until)).tz(websiteTimezone),
        currentSessionMomentDate,
        websiteId,
        websiteTimezone,
        websiteInstallDate,
        hasActiveFreePlan,
      );
    }

    return { from, until };
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

function getDatepickerMinDate({
  hasFreePlan,
  installDate,
  timezone,
}: {
  hasFreePlan: boolean;
  installDate: number;
  timezone?: string;
}) {
  const today = moment()
    .tz(timezone ?? window.timezone)
    .endOf('day');
  const freePlanHistoryStartDate = today.subtract(FREE_PLAN_HISTORY_DAYS - 1, 'days');
  const installDateInstance = moment.unix(installDate).tz(timezone ?? window.timezone);

  if (hasFreePlan && freePlanHistoryStartDate.isAfter(installDateInstance)) {
    return freePlanHistoryStartDate;
  }

  return installDateInstance;
}

export function updateSelectedPeriod(
  from: moment.Moment,
  until: moment.Moment,
  currentSessionDate: moment.Moment,
  websiteId: string,
  websiteTimezone: string,
  websiteInstallDate: number,
  hasFreePlan: boolean,
) {
  const momentWithTimezone = () => moment().tz(websiteTimezone);
  const yesterday = momentWithTimezone().subtract(1, 'days');
  const last7DaysStart = momentWithTimezone().subtract(7, 'days');
  const last30DaysStart = momentWithTimezone().subtract(30, 'days');
  const thisMonthStart = momentWithTimezone().startOf('month');
  const last6MonthsStart = momentWithTimezone().date(moment().get('date')).subtract(6, 'months');
  const last12MonthsStart = moment().tz(websiteTimezone).date(moment().get('date')).subtract(12, 'months');
  const thisYearStart = momentWithTimezone().startOf('year');

  let updatedDate = { from, until };

  const isToday = from.isSame(currentSessionDate, 'day') && until.isSame(currentSessionDate, 'day');
  const isYesterday = from.isSame(yesterday, 'day') && until.isSame(yesterday, 'day');
  const isLast7Days = from.isSame(last7DaysStart, 'day') && until.isSame(currentSessionDate, 'day');
  const isLast30Days = from.isSame(last30DaysStart, 'day') && until.isSame(currentSessionDate, 'day');
  const isThisMonth = from.isSame(thisMonthStart, 'day') && until.isSame(currentSessionDate, 'day');
  const isLast6Months = from.isSame(last6MonthsStart, 'day') && until.isSame(currentSessionDate, 'day');
  const isLast12Months = from.isSame(last12MonthsStart, 'day') && until.isSame(currentSessionDate, 'day');
  const isThisYear = from.isSame(thisYearStart, 'day') && until.isSame(currentSessionDate, 'day');
  const isInstallPeriodSelected =
    from.isSame(moment.unix(websiteInstallDate).tz(websiteTimezone), 'day') && until.isSame(currentSessionDate, 'day');

  if (isToday || isYesterday || isLast7Days || isLast30Days || isLast6Months || isLast12Months) {
    updatedDate = {
      from: from.add(1, 'day').startOf('day'),
      until: until.add(1, 'day').endOf('day'),
    };
  } else if (isThisMonth || isThisYear || isInstallPeriodSelected) {
    updatedDate = {
      from,
      until: until.add(1, 'day').endOf('day'),
    };
  }
  storeTimePeriodToLocalStorage({
    websiteId,
    installDate: websiteInstallDate,
    hasFreePlan,
    ...updatedDate,
  });
  return updatedDate;
}

export function extractDomainFromEmail(email: string): string {
  if (typeof email !== 'string') return '';

  const parts = email.split('@');
  if (parts.length !== 2) return '';

  return parts[1];
}

export function updateFavicon(iconURL: string) {
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

  if (!link) {
    link = document.createElement('link') as HTMLLinkElement;
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.href = iconURL;
}
